import { motion, AnimatePresence } from "framer-motion";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Eye, Pencil, Trash2, Users, Copy } from "lucide-react";
import { SupervisorBadge } from "./SupervisorBadge";
import { toast } from "sonner";
import { STATUS_STYLES } from "../lib/constants";
import { StatusIcon, Countdown, mostUrgentDoc } from "./DocStatus";
import { EmployeeAvatar } from "./EmployeeAvatar";
import { useLang } from "../i18n/LanguageContext";

export const EmployeeTable = ({ employees, onView, onEdit, onDelete, readOnly = false, onRefresh, duplicateIqamas, selected, onToggle, onTogglePage, onFilterStatus, onOpenFile }) => {
  const { t } = useLang();
  if (!employees.length) {
    return (
      <div data-testid="empty-state" className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
        <Users className="mx-auto h-10 w-10 text-slate-300" />
        <p className="mt-3 font-heading text-lg text-slate-600">{t("dash.emp.noTitle")}</p>
        <p className="text-sm text-slate-400">
          {readOnly ? t("dash.emp.noReadonly") : t("dash.emp.noAddFirst")}
        </p>
      </div>
    );
  }
  const sel = !readOnly && selected && onToggle;
  const pageAll = sel && employees.every((e) => selected.has(e.id));
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
            {sel && <TableHead className="w-10"><input type="checkbox" data-testid="employee-select-page" checked={pageAll} onChange={() => onTogglePage(employees.map((e) => e.id), !pageAll)} className="h-4 w-4 accent-blue-600 cursor-pointer" /></TableHead>}
            <TableHead className="text-right font-heading text-slate-600">{t("dash.emp.colEmployee")}</TableHead>
            <TableHead className="text-right font-heading text-slate-600">{t("dash.emp.colBranch")}</TableHead>
            <TableHead className="text-right font-heading text-slate-600">{t("dash.emp.colNumber")}</TableHead>
            <TableHead className="text-right font-heading text-slate-600 whitespace-nowrap">العقد</TableHead>
            <TableHead className="text-right font-heading text-slate-600">{t("dash.emp.colNearest")}</TableHead>
            <TableHead className="text-right font-heading text-slate-600">{t("dash.emp.colStatus")}</TableHead>
            {!readOnly && <TableHead className="text-right font-heading text-slate-600">{t("dash.emp.colActions")}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          <AnimatePresence>
            {employees.map((e) => (
              <motion.tr key={e.id} data-testid={`employee-row-${e.id}`}
                layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className={`border-b border-slate-100 hover:bg-slate-50/60 transition-colors cursor-pointer ${sel && selected.has(e.id) ? "bg-blue-50/60" : ""}`}
                onClick={() => onView(e)}>
                {sel && (
                  <TableCell onClick={(ev) => ev.stopPropagation()} className="w-10">
                    <input type="checkbox" data-testid={`employee-select-${e.id}`} checked={selected.has(e.id)} onChange={() => onToggle(e.id)} className="h-4 w-4 accent-blue-600 cursor-pointer" />
                  </TableCell>
                )}
                <TableCell className="font-semibold text-slate-800">
                  <div className="flex items-center gap-2.5">
                    <EmployeeAvatar employee={e} size="sm" editable={!readOnly} onUploaded={onRefresh} />
                    <div>
                      <div className="flex items-center gap-1.5">
                        {e.name}
                        <SupervisorBadge employee={e} />
                        {duplicateIqamas?.has((e.iqama_number || "").trim()) && (
                          <span data-testid={`employee-duplicate-${e.id}`} title={t("dash.emp.duplicateTitle")}
                            className="inline-flex items-center gap-0.5 rounded-full bg-red-50 border border-red-200 text-red-600 text-[10px] font-semibold px-1.5 py-0.5">
                            <Copy className="h-2.5 w-2.5" /> {t("dash.emp.duplicate")}
                          </span>
                        )}
                      </div>
                      {(e.profession || e.app_vehicle_type) ? (
                        <span className="flex flex-wrap items-center gap-1.5 text-xs font-normal text-slate-400">
                          {e.profession}
                          {e.app_vehicle_type && <span data-testid={`employee-appveh-${e.id}`} className="whitespace-nowrap rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">{e.app_vehicle_type}</span>}
                        </span>
                      ) : null}
                      {(e.pending_documents || []).some((r) => r.status === "pending") && (
                        <span data-testid={`employee-pending-docs-${e.id}`} className="mt-0.5 inline-block rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">وثيقة بانتظار الموافقة</span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-slate-600">{e.branch || "—"}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <div className="flex flex-col gap-0.5 leading-tight">
                    <div className="flex items-center gap-1 text-slate-600"><span data-testid={`employee-rider-id-${e.id}`} className="font-mono-nums">{e.rider_id || "—"}</span>
                      {e.rider_id && <button type="button" data-testid={`copy-rider-id-${e.id}`} title="نسخ رقم المعرف" className="rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-blue-600"
                        onClick={(ev) => { ev.stopPropagation(); navigator.clipboard.writeText(String(e.rider_id)).then(() => toast.success("تم نسخ رقم المعرف")); }}><Copy className="h-3 w-3" /></button>}
                    </div>
                    {e.iqama_number && <div data-testid={`employee-iqama-${e.id}`} className="text-[11px] text-slate-400">إقامة: <span className="font-mono-nums">{e.iqama_number}</span></div>}
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap" data-testid={`employee-contract-${e.id}`}>
                  {e.contract_start || e.contract_end ? (
                    <div className="text-[11px] leading-5 text-slate-600 font-mono-nums" dir="ltr">
                      <div>{e.contract_start || "—"}</div>
                      <div className="flex items-center gap-1">{e.contract_end || "—"}{e.contract_status?.status && e.contract_status.status !== "valid" && e.contract_status.status !== "none" && <Countdown doc={e.contract_status} />}</div>
                    </div>
                  ) : <span className="text-slate-300">—</span>}
                </TableCell>
                <TableCell>
                  {(() => {
                    const u = mostUrgentDoc(e);
                    if (!u) return <span className="text-slate-400 text-sm">{t("dash.emp.noDatedDocs")}</span>;
                    return (
                      <div className={`flex items-center gap-1.5 flex-wrap ${u.attachment_id && onOpenFile ? "cursor-pointer rounded-md px-1 hover:bg-blue-50" : ""}`} data-testid={`employee-urgent-${e.id}`}
                        title={u.attachment_id ? "فتح المستند" : undefined}
                        onClick={u.attachment_id && onOpenFile ? (ev) => { ev.stopPropagation(); onOpenFile(u, e); } : undefined}>
                        <StatusIcon status={u.status} />
                        <span className="text-xs text-slate-600">{u.label}</span>
                        <Countdown doc={u} />
                        {e.expiring_count > 1 && (
                          <span className="text-[11px] text-slate-400">{t("dash.emp.moreOthers", { n: e.expiring_count - 1 })}</span>
                        )}
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Badge data-testid={`employee-status-${e.id}`} variant="outline"
                    onClick={onFilterStatus ? (ev) => { ev.stopPropagation(); onFilterStatus(e.overall_status); } : undefined}
                    title={onFilterStatus ? "عرض كل الموظفين بنفس الحالة" : undefined}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold ${onFilterStatus ? "cursor-pointer hover:opacity-80" : ""} ${STATUS_STYLES[e.overall_status]}`}>
                    {t(`dash.status.${e.overall_status}`)}
                  </Badge>
                </TableCell>
                {!readOnly && (
                  <TableCell onClick={(ev) => ev.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" data-testid={`view-employee-${e.id}`}
                        className="h-8 w-8 text-slate-500 hover:text-slate-800" onClick={() => onView(e)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" data-testid={`edit-employee-${e.id}`}
                        className="h-8 w-8 text-slate-500 hover:text-blue-600" onClick={() => onEdit(e)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" data-testid={`delete-employee-${e.id}`}
                        className="h-8 w-8 text-slate-500 hover:text-red-600" onClick={() => onDelete(e)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </motion.tr>
            ))}
          </AnimatePresence>
        </TableBody>
      </Table>
    </div>
  );
};
